import { createModulePermissionMethods } from "../../admin-shared/modulePermissions";
import { orgEntitlementAccessMixin } from "../../admin-shared/orgEntitlementAccess";

const parseJwtPayload = (token) => {
  if (!token || typeof token !== "string") return null;
  const parts = token.split(".");
  if (parts.length < 2) return null;

  try {
    const base64 = parts[1].replace(/-/g, "+").replace(/_/g, "/");
    const padded = base64.padEnd(base64.length + ((4 - (base64.length % 4)) % 4), "=");
    return JSON.parse(atob(padded));
  } catch {
    return null;
  }
};

const resolveRole = () => {
  const stored = (localStorage.getItem("naturals_partner_role") || "").toLowerCase();
  if (
    stored === "super_admin" ||
    stored === "admin" ||
    stored === "user" ||
    stored === "dashboard_staff"
  ) {
    return stored;
  }
  return "user";
};

/** Keys that previously lived under edit_set_flow — inherit when absent. */
const SET_FLOW_CHILDREN = new Set([
  "manage_kiosk_landings",
  "manage_terms",
  "manage_skin_settings",
]);

const MODULE_KEY = "naturals_partner_accessModules";

let _moduleGate = null;
function moduleGate() {
  if (!_moduleGate) {
    _moduleGate = createModulePermissionMethods(MODULE_KEY, {
      isSuperAdmin: () => permissionService.isSuperAdmin(),
      isAdmin: () => permissionService.isAdmin(),
      isFranchise: () => permissionService.isFranchise(),
    });
  }
  return _moduleGate;
}

export const permissionService = {
  getRole() {
    const role = resolveRole();
    localStorage.setItem("naturals_partner_role", role);
    return role;
  },

  getAllowedModules() {
    return moduleGate().getAllowedModules();
  },

  hasAny(...args) {
    return moduleGate().hasAny(...args);
  },

  hasAll(...args) {
    return moduleGate().hasAll(...args);
  },

  has(moduleId) {
    return moduleGate().has(moduleId);
  },

  /** HQ Admin or Super Admin (shared admin URL). */
  isAdmin() {
    const role = this.getRole();
    return role === "admin" || role === "super_admin";
  },

  isSuperAdmin() {
    return this.getRole() === "super_admin";
  },

  /** Franchise store owner login (separate URL). */
  isFranchise() {
    return this.getRole() === "user";
  },

  /** View-only staff on the partner dashboard (operations, guests, team, reels). */
  isDashboardStaff() {
    return this.getRole() === "dashboard_staff";
  },

  canManageDashboardStaff() {
    return this.isFranchise();
  },

  canAccessPartnerPath(pathname) {
    if (!this.isDashboardStaff()) return true;
    const path = String(pathname || "").replace(/\/$/, "");
    const base = "/nyafaz-ep/partner";
    if (path === base || path === `${base}/overview` || path.startsWith(`${base}/overview/`)) {
      return true;
    }
    const allowed = [
      "/check-ins",
      "/operations/",
      "/visit-services",
      "/customers",
      "/check-in-analytics",
      "/customer-analytics",
      "/flow-analytics",
      "/staff-report",
      "/staff-availability",
      "/staff",
      "/edit-profile",
    ];
    return allowed.some((seg) => path.startsWith(`${base}${seg}`));
  },

  /** Only Admin (not Super Admin) creates franchise users. */
  canCreateFranchiseUsers() {
    if (this.getRole() !== "admin") return false;
    return this.getAdminPermission("create_franchise_users", true);
  },

  getAdminPermissions() {
    try {
      const raw = localStorage.getItem("naturals_partner_admin_permissions");
      if (!raw) return {};
      const perms = JSON.parse(raw);
      return perms && typeof perms === "object" ? perms : {};
    } catch {
      return {};
    }
  },

  getAdminPermission(key, defaultValue = false) {
    if (this.isSuperAdmin()) return true;
    const perms = this.getAdminPermissions();
    if (typeof perms[key] === "boolean") return perms[key];
    if (SET_FLOW_CHILDREN.has(key) && typeof perms.edit_set_flow === "boolean") {
      return perms.edit_set_flow;
    }
    if (key === "manage_ai_chat") {
      if (typeof perms.manage_skin_settings === "boolean") return perms.manage_skin_settings;
      if (typeof perms.edit_set_flow === "boolean") return perms.edit_set_flow;
    }
    const raw = localStorage.getItem("naturals_partner_admin_permissions");
    if (raw === null && this.isAdmin()) return defaultValue;
    return false;
  },

  canUseWhatsappCredentials() {
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("whatsapp_credentials", false);
  },

  canEditSetFlow() {
    if (this.isFranchise()) return true;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("edit_set_flow", true);
  },

  canManageKioskLandings() {
    if (this.isFranchise()) return false;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("manage_kiosk_landings", true);
  },

  canManageTerms() {
    if (this.isFranchise()) return false;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("manage_terms", true);
  },

  canManageSkinSettings() {
    if (this.isFranchise()) return false;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("manage_skin_settings", true);
  },

  canManageAiChat() {
    if (this.isFranchise()) return false;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("manage_ai_chat", true);
  },

  canResolveComplaints() {
    if (this.isFranchise()) return false;
    if (this.isSuperAdmin()) return true;
    return this.getAdminPermission("resolve_complaints", true);
  },

  canViewComplaints() {
    if (this.isFranchise()) return true;
    if (this.isSuperAdmin()) return true;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("view_complaints", true);
  },

  canManageProducts() {
    if (this.isFranchise()) return true;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("manage_products", false);
  },

  canManageServices() {
    if (this.isFranchise()) return false;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("manage_services", false);
  },

  canViewDashboard() {
    if (this.isFranchise()) return true;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("view_dashboard", true);
  },

  canViewStoreKpis() {
    if (this.isFranchise()) return true;
    if (this.isSuperAdmin()) return true;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("view_store_kpis", true);
  },

  canViewContactMessages() {
    if (this.isFranchise()) return false;
    if (this.isSuperAdmin()) return true;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("view_contact_messages", true);
  },

  canEditCompanyProfile() {
    if (this.isFranchise()) return false;
    if (!this.isAdmin()) return false;
    return this.getAdminPermission("company_profile", true);
  },

  getPlan() {
    return (localStorage.getItem("naturals_partner_plan") || "").toLowerCase();
  },

  getPlanStatus() {
    return (localStorage.getItem("naturals_partner_plan_status") || "active").toLowerCase();
  },

  getDisplayPlanLabel() {
    const plan = this.getPlan();
    const status = this.getPlanStatus();
    const formattedPlan = plan ? plan.charAt(0).toUpperCase() + plan.slice(1) : "Free";
    if (status === "trial_active") return `Trial - ${formattedPlan}`;
    return `${formattedPlan} Plan`;
  },

  ...orgEntitlementAccessMixin("partner"),
};
